import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { Skill } from '../types';

export const createSlideNarrationVideoSkill: Skill = {
    name: SKILL_NAMES.createSlideNarrationVideo,
    description:
        'Narrate an existing slide deck (PowerPoint, PDF, or slide images): each slide shown whole and unaltered, with an AI voiceover timed to it. Use for presentations, webinar decks, training slides, and conference talks the user wants voiced.',
    kind: 'video',
    surfaces: ['sketch'],
    dashboard: {
        title: 'Narrated Slides',
        preview: 'narrated-slides',
        frames: [
            'narrated-slides-1',
            'narrated-slides-2',
            'narrated-slides-3',
            'narrated-slides-4',
            'narrated-slides-5',
        ],
    },
    get instructions(): string {
        return `# Create Slide Narration Video

For adding narration to a deck the user already made, attached as a **PowerPoint or
PDF**, or as one image per slide. The slides are the finished artwork: each one appears
whole and unaltered — never cropped — while an AI voiceover explains it. This format inverts the usual default — normally a
script drives a redesign, here the slides are fixed and the narration is fitted
to them, whether the user supplies that narration or it is written from the deck.
Never redesign, restyle, summarize onto new layouts, or rebuild a slide's content
as template text: doing so is the one defect that makes this format worthless to
the user.

The deck also sets the video's length. There is no target duration to hit — the
video runs as long as it takes to narrate every slide.

## Setup
Load \`${SKILL_NAMES.documentBasics}\` first — the deck is an attached document, and that
skill covers scoping it and showing figures at maximum visibility. Then use
\`${SKILL_NAMES.gatherRequirements}\` for anything still missing, skipping what's already
provided. Specifically confirm:
- **Where the narration comes from** (\`radio\`): written from what's on the slides,
  or from a script the user supplies. Ask this first — a presenter often already
  has their talk written, and it is always better than anything derived from
  bullet points. If they choose their own script and haven't attached it yet, ask
  for it and wait rather than writing one to fill the gap. Skip this question when
  they already provided a script with the deck.
- **Tone and audience** for the narration, so the script isn't generic. Skip this
  when the user is supplying their own script — their wording already settles it.
- **Which slides to skip.** Don't ask this as an open question — look through the
  deck, pick out the slides that are candidates for skipping, and offer exactly
  those as a \`checkbox\` question (\`${TOOL_NAMES.askUserQuestion}\`, type \`checkbox\`, so any
  number can be picked) with one choice per candidate, each labelled by its slide
  number and title so it's recognisable. Candidates are the slides that carry no
  narratable content of their own: agenda and section-divider cards, thank-you and
  Q&A cards, a repeated title or brand card, and backup or appendix slides after
  the closing. Offer nothing that carries real content, and skip the question
  entirely when the deck has no such slides.
- **Full-bleed or inset** — whether each slide should fill the frame or sit inset
  on the background with a border (\`radio\`). See "Framing" below for what each
  means and when inset is forced regardless of the answer.
- **Voice**, if they have a preference (\`${TOOL_NAMES.searchVoices}\`). Set
  \`require_voiceover: true\` on that question — narrating the deck is the whole
  point of this format, so "No voiceover" isn't on offer.

Overrides to those skills for this format:
- **Don't ask about aspect ratio.** Match it to the slides' own shape — a standard
  deck is landscape — and set it with \`${TOOL_NAMES.setAspectRatio}\` BEFORE building any
  scenes. A slide forced into a portrait frame is either cropped or pillarboxed;
  neither is acceptable.
- **Don't ask about duration.** The narration decides it.
- **Don't offer a template redesign.** Templates only supply the voice-over scene
  group's incidental styling here; the slides are the visuals.

### Getting the slides
A deck attached as a **PDF or a PowerPoint** (\`.pptx\`) gives you a picture of each
slide through \`${TOOL_NAMES.documentExtract}\`: request a page's \`screenshot\` and you get
the slide exactly as designed, registered as media you can place with
\`${TOOL_NAMES.setMedia}\`. Slides are addressed **by page number** — slide 4 is page 4 —
so there is no name or position to match on.

Work through the deck in order: get the outline first (no pages) to learn how many
slides there are and what each one is, then request the slides you are keeping. Ask
for them in batches rather than one call per slide.

**Ask for a slide's \`screenshot\`, never its \`embedded_images\`.** The embedded images
are the logos and photos lifted out of a slide, not the slide — a video built from
those shows fragments of the deck instead of the deck, which is exactly the failure
this format exists to avoid.

When the user attached slide images themselves rather than a deck, go by their
filenames and the order they run in; load \`${SKILL_NAMES.findUserMedia}\` if you can't
find them.

## Structure
- **One voiceover scene group for the whole video** (\`${TOOL_NAMES.addVoiceoverSceneGroup}\`),
  never one per slide.
- **One overlay per slide** (\`${TOOL_NAMES.addVoiceoverScene}\`), in deck order, each covering
  the words that narrate that slide. Pass every overlay in ONE call as a \`scenes\`
  array — not one call per slide.
- Slide count sets scene count. A slide the user asked to skip gets no scene and no
  narration; every slide that is in scope gets exactly one.

## The narration
Get the full script settled first, then generate it as one voiceover.

### When the user supplies the script
Their words are the script — use them as written. Don't rewrite, tighten,
re-order, or "improve" the phrasing, and don't top it up with anything you read
off the slides; a presenter's own talk is the thing they came here to narrate.
- **Split it across the slides, don't change it.** Work out which stretch of their
  script belongs to each slide and time the overlays to that (see below). The words
  themselves stay untouched.
- Fix only what would be read aloud wrongly — a stray heading, a slide number, or a
  stage direction like "(pause here)" that was never meant to be spoken. Say what
  you dropped.
- **If the script doesn't line up with the deck**, say so rather than papering over
  it: it may cover slides that aren't there, stop partway, or not map cleanly. Ask
  how they want to handle the mismatch instead of inventing narration for an
  uncovered slide or silently dropping a slide that has none.

### When you write it from the slides
- **Narrate the slide, don't read it aloud.** A slide's bullets are prompts for a
  speaker; spoken verbatim they sound robotic. Say what the bullets mean, in
  connected sentences.
- **Write from what the slide shows, not just its text.** Look at the rendered
  slide: a chart, diagram, or logo wall may carry almost no extractable text while
  being the whole point of that slide.
- **Ignore boilerplate.** Repeated footers, standing taglines, slide numbers, and
  confidentiality notices appear on every slide and belong in none of the narration.
- **Carry the deck across slides.** Brief transitions ("that raises the question of
  …") make a sequence of slides play as one talk. Don't restate the slide's title
  as the first words of its narration.
- **Length follows the slide.** A dense slide earns several sentences; a title or
  section-divider slide earns one short line. Never pad a thin slide to match a
  dense one — uneven per-slide narration is correct here.

## Asset timing — every slide holds exactly its own narration
The overlay for a slide must cover precisely the words that talk about it, so the
slide on screen is always the slide being discussed. Getting this wrong is the
format's characteristic failure: the voice describes one slide while another is
showing.
- Take the word range for each slide's overlay from the sentences that belong to it
  — the ones you wrote for it, or the stretch of the user's own script that covers
  it.
- **Overlays must tile the whole transcript with no gaps.** Any uncovered word
  renders as a raw waveform instead of a slide. The first overlay starts on the
  first word, the last ends on the last, and each begins where the previous ended.
- Re-time a slide that drifted with \`${TOOL_NAMES.setSceneRange}\`; don't delete and rebuild the
  run.

## Media
- **Use a media-only design with no template text**, set the slide image with
  \`${TOOL_NAMES.setMedia}\`, and set \`kenburn\` to \`none\` — a slide is a figure, and pan/zoom
  crops text off its edges and makes it unreadable.

### Framing — the whole slide must be visible
A slide is a figure: the entire thing has to be on screen. Any crop cuts off text
or part of a chart, which is a defect no matter how good the rest looks.
\`${SKILL_NAMES.cropMedia}\` covers the \`scale\`/position mechanics; the rules specific to
this format are:
- **Check every slide for cropping** rather than assuming the design fits it.
  Compare the slide's own proportions against the frame's, and run
  \`${TOOL_NAMES.reviewSceneImages}\` on the scene — it flags anything cut off at an edge.
- **If a slide is cropped, zoom out** (\`${TOOL_NAMES.setMedia}\` with \`scale\` below 100) until
  the whole slide is visible. Never leave a slide cropped to preserve a full-bleed
  look — fitting the slide wins over filling the frame, so this **overrides a
  full-bleed answer**: a slide that can't fill the frame without cropping becomes
  inset whatever the user picked.
- **Inset framing leaves roughly a 10% border** of the background showing around
  the slide. Zoom out to about that margin — enough to read as a deliberate frame
  rather than a slide that failed to fit.
- **Every scene gets the same framing.** One scale for all slides of the same
  proportions, so the deck doesn't jump size between scenes. When one slide has to
  zoom out further than the rest (an odd-sized slide), apply that same scale to
  every scene rather than letting one sit smaller than its neighbours.
- **Full brightness, no dimming.** Any scrim or darkening the design applies is a
  defect here; the slide must render exactly as designed.
- **Never add stock media.** The deck supplies every visual. A stock photo beside
  the user's own slide is a defect, not an enhancement.
- **Each slide appears in exactly one scene**, and slides run in deck order.

## Styling
- Colors, fonts, and brand come from the slides themselves, which are already
  designed — don't apply a brand kit or hand-pick color overrides to compete with
  them.
- **Captions are off by default** (\`${TOOL_NAMES.setCaptions}\`) — slides usually carry text
  along the bottom, and captions collide with it. Enable them only if the user asks,
  and check they don't cover slide content.

## Finishing
Load \`${SKILL_NAMES.reviewVideo}\` for the standard pass. On top of that, confirm with
\`${TOOL_NAMES.getVideoState}\`:
- Every in-scope slide appears exactly once, in deck order, one scene each — and no
  image that was merely embedded inside a slide was used as a scene's visual.
- **Every slide is fully visible — nothing cropped at any edge.** Run
  \`${TOOL_NAMES.reviewSceneImages}\` on each scene, not just the first: this is the defect
  most likely to survive to the finished video.
- Every scene shares one framing (the same \`scale\`), and an inset slide sits with
  roughly a 10% border rather than an accidental sliver.
- Every slide has \`kenburn\` set to \`none\` and no dimming.
- No scene carries template headline or body text over a slide, and no stock media
  appears anywhere in the video.
- Overlays cover the transcript end to end with no gaps, and each slide's words are
  the words about that slide — check a slide mid-deck, not just the first.
- The aspect ratio matches the slides' own shape.
- Any slide the user chose to skip has no scene and no narration, and every slide
  they kept has both.
- If the user supplied the script, the spoken words are theirs — verbatim, with
  nothing rewritten, added, or dropped beyond the unspeakable bits you flagged.`;
    },
};
